<template>
  <div class="row">
    <div class="small-12 medium-8 columns">
      <form class="row" @submit.prevent="handleSubmit">
        <woot-input
          v-model.trim="title"
          :class="{ error: $v.title.$error }"
          class="medium-12 columns"
          :label="$t('TEAMS_SETTINGS.FORM.NAME.LABEL')"
          :placeholder="$t('TEAMS_SETTINGS.FORM.NAME.PLACEHOLDER')"
          @input="$v.title.$touch"
        />

        <woot-input
          v-model.trim="description"
          :class="{ error: $v.description.$error }"
          class="medium-12 columns"
          :label="$t('TEAMS_SETTINGS.FORM.DESCRIPTION.LABEL')"
          :placeholder="$t('TEAMS_SETTINGS.FORM.DESCRIPTION.PLACEHOLDER')"
          @input="$v.description.$touch"
        />

        <div class="medium-12">
          <input v-model="allowAutoAssign" type="checkbox" :value="true" />
          <label for="conversation_creation">
            {{ $t('TEAMS_SETTINGS.FORM.AUTO_ASSIGN.LABEL') }}
          </label>
        </div>

        <div v-if="allowAutoAssign" class="medium-12">
          <label for="conversation_creation">
            {{ $t('TEAMS_SETTINGS.FORM.MODE_ASSIGN.LABEL') }}
            <select v-model="assignType">
              <option
                v-for="atype in assignTypes"
                :key="atype.id"
                :value="atype.key"
              >
                {{ atype.value.toLowerCase() }}
              </option>
            </select>
          </label>
        </div>

        <div class="medium-12">
          <label>
            {{ $t('TEAMS_SETTINGS.FORM.SUPERVISOR.LABEL') }}
            <select v-model="supervisorId">
              <option value=""></option>
              <option
                v-for="user in teamSupervisors"
                :key="user.id"
                :value="user.id"
              >
                {{ user.name }}
              </option>
            </select>
          </label>
        </div>

        <div v-if="allowAutoAssign" class="max-assignment-container">
          <woot-input
            v-model.trim="maxAssignmentLimit"
            type="number"
            :class="{ error: $v.maxAssignmentLimit.$error }"
            :error="maxAssignmentLimitErrors"
            :label="
              $t('TEAMS_SETTINGS.FORM.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT')
            "
            @blur="$v.maxAssignmentLimit.$touch"
          />
          <p class="help-text">
            {{
              $t(
                'TEAMS_SETTINGS.FORM.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT_SUB_TEXT'
              )
            }}
          </p>
        </div>

        <div class="medium-12">
          <div class="required-attribute-wrapper">
            <div class="required-header">
              <h4 class="required-title">
                {{ $t('TEAMS_SETTINGS.FORM.PRIVATE.LABEL') }}
              </h4>

              <p class="required-description">
                {{ $t('TEAMS_SETTINGS.FORM.PRIVATE.DESCRIPTION') }}
              </p>

              <label class="switch">
                <input v-model="isPrivate" type="checkbox" />
                <span class="slider"></span>
              </label>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <div class="medium-12 columns">
            <woot-submit-button
              :disabled="$v.title.$invalid || submitInProgress"
              :button-text="submitButtonText"
              :loading="submitInProgress"
            />
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import WootSubmitButton from '../../../../components/buttons/FormSubmitButton';
import { minValue } from 'vuelidate/lib/validators';
import validations from './helpers/validations';
import { mapGetters } from 'vuex';

export default {
  components: {
    WootSubmitButton,
  },
  props: {
    onSubmit: { type: Function, default: () => {} },
    submitInProgress: { type: Boolean, default: false },
    formData: { type: Object, default: () => {} },
    submitButtonText: { type: String, default: '' },
  },
  data() {
    const formData = this.formData || {};
    const {
      description = '',
      name: title = '',
      allow_auto_assign: allowAutoAssign = true,
      assign_type: assignType = 'random_online_agent_only',
      private: isPrivate = false,
      max_assignment_limit: maxAssignmentLimit = null,
      supervisor_id: supervisorId = '',
    } = formData;

    return {
      description,
      title,
      allowAutoAssign,
      assignType,
      isPrivate,
      maxAssignmentLimit,
      supervisorId,
    };
  },
  validations: {
    ...validations,
    maxAssignmentLimit: { minValue: minValue(1) },
  },
  computed: {
    ...mapGetters({
      agentList: 'agents/getAgents',
    }),
    teamSupervisors() {
      return this.agentList.filter(agent => agent.role === 'team_supervisor');
    },
    maxAssignmentLimitErrors() {
      if (this.$v.maxAssignmentLimit.$error) {
        return this.$t(
          'TEAMS_SETTINGS.FORM.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT_RANGE_ERROR'
        );
      }
      return '';
    },
    assignTypes() {
      return [
        {
          id: 1,
          key: 'random_online_agent_only',
          value: this.$t(
            'TEAMS_SETTINGS.FORM.ASSIGN_TYPES.RANDOM_ONLINE_AGENT_ONLY'
          ),
        },
        {
          id: 2,
          key: 'random_all_agent',
          value: this.$t('TEAMS_SETTINGS.FORM.ASSIGN_TYPES.RANDOM_ALL_AGENT'),
        },
        {
          id: 3,
          key: 'sequential_online_agent_only',
          value: this.$t(
            'TEAMS_SETTINGS.FORM.ASSIGN_TYPES.SEQUENTIAL_ONLINE_AGENT_ONLY'
          ),
        },
        {
          id: 4,
          key: 'sequential_all_agent',
          value: this.$t(
            'TEAMS_SETTINGS.FORM.ASSIGN_TYPES.SEQUENTIAL_ALL_AGENT'
          ),
        },
      ];
    },
  },
  created() {
    this.$store.dispatch('agents/get');
  },
  methods: {
    handleSubmit() {
      this.$v.$touch();
      if (this.$v.$invalid) return;

      this.onSubmit({
        description: this.description,
        name: this.title,
        allow_auto_assign: this.allowAutoAssign,
        assign_type: this.assignType,
        private: this.isPrivate,
        max_assignment_limit: this.maxAssignmentLimit,
        supervisor_id: this.supervisorId,
      });
    },
  },
};
</script>

<style scoped lang="scss">
.max-assignment-container {
  padding-bottom: 0;
}
.modal-footer {
  display: flex;
  justify-content: flex-end;
  width: 100%;
}
.woot-submit-button {
  width: 100%;
  max-width: 200px;
}
.required-attribute-wrapper {
  margin-top: 12px;
  margin-bottom: var(--space-large);
  padding: 16px;
  border: 1px solid var(--s-100);
  border-radius: 12px;
  background: var(--white);
}

.required-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.required-title {
  margin: 0 0 10px;
  font-size: var(--font-size-medium);
  font-weight: var(--font-weight-semibold);
  color: var(--s-900);
}

.switch {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 56px;
  height: 32px;
  cursor: pointer;
}

.switch input {
  display: none;
}

.slider {
  position: relative;
  width: 56px;
  height: 32px;
  background-color: #cbd5e1;
  border-radius: 999px;
  transition: background-color 0.3s ease;
}

.slider::before {
  content: '';
  position: absolute;
  width: 24px;
  height: 24px;
  left: 4px;
  top: 4px;
  border-radius: 50%;
  background-color: #ffffff;
  transition: transform 0.3s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
}

.switch input:checked + .slider {
  background-color: rgb(19, 88, 153);
}

.switch input:checked + .slider::before {
  transform: translateX(24px);
}
</style>
